<template>
  <v-container>
    <h1>{{ this.title }}</h1>
    <v-row>
      <v-col cols="6">
        <v-text-field
          v-model="search"
          label="Search by Unique ID"
          prepend-icon="mdi-magnify"
          @input="fetchTasks"
        ></v-text-field>
      </v-col>
      <v-col cols="3">
        <v-select
          v-model="order"
          :items="[
            { text: 'Task Create Time', order_by: 'id', value: 'desc', id: 'create_desc' },
            { text: 'Newest Process Time', order_by: 'process_time', value: 'desc', id: 'process_time_desc' },
            { text: 'Oldest Process Time', order_by: 'process_time', value: 'asc', id: 'process_time_asc' }
          ]"
          label="Sorting"
          item-text="text"
          item-value="id"
          return-object
          @change="fetchTasks"
        ></v-select>
      </v-col>
    </v-row>
    <v-data-table
      :headers="filter_headers()"
      :items="tasks"
      :items-per-page="perPage"
      :page="page"
      :server-items-length="totalTasks"
      :loading="loading"
      @update:page="updatePage"
      @update:items-per-page="updateItemsPerPage"
    >
      <template v-slot:item.progress_step="{ item }">
        <task-step-progress
          :progress-step="item.progress_step"
          :steps="progressStepList"
        ></task-step-progress>
      </template>
      <template v-slot:item.status="{ item }">
        {{ formatStatus(item.progress_step.displayStatus || item.status) }}
      </template>      
      <template v-slot:item.process_time="{ item }">
        {{ item.process_time ? formatTime(item.process_time) : '-' }}
      </template>
      <template v-slot:item.files="{ item }">
        <v-btn
          color="primary"
          small
          :href="`${$http.defaults.baseURL}/api/tasks/${item.id}/originals`"
          target="_blank"
          class="mr-2"
        >
          Original
        </v-btn>
      </template>
      <template v-slot:item.reextract="{ item }">
        <v-btn
          color="primary"
          small
          @click="restartTask(item.id)"
          :disabled="!['completed', 'validate failed', 'analysis failed'].includes((item.progress_step.displayStatus ?? '').toLowerCase())"
        >
          Re-extract
        </v-btn>
      </template>
      <template v-slot:item.review="{ item }">
        <v-btn
          color="primary"
          small
          @click="editTaskData(item.id, item.unique_id)"
          :disabled="!['completed', 'validate failed'].includes((item.progress_step.displayStatus ?? '').toLowerCase())"
        >
          Review
        </v-btn>
      </template>
      <template v-slot:item.show_result="{ item }">
        <v-btn
          color="primary"
          small
          @click="showResult(item.id, item.unique_id)"
          :disabled="(item.progress_step.displayStatus ?? '').toLowerCase() != 'completed'"
        >
          Result
        </v-btn>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import axios from 'axios'
import TaskStepProgress from '@/components/TaskStepProgress';
import { progressStepList, updateTaskProgressStep } from '@/utils/taskProgressUtils';

export default {
  name: 'TaskList',
  components: {
    'task-step-progress': TaskStepProgress
  },
  props:{
    title: {
      type: String
    },
    taskType: {
      type: String
    },
  },
  data() {
    return {
      tasks: [],
      search: '',
      order: { text: 'Create Order', order_by: 'id', value: 'desc', id: 'create_desc' },
      page: 1,
      perPage: 10,
      totalTasks: 0,
      loading: false,
      showReviewColumn: this.$route.query.show_review != null,
      progressStepList: progressStepList,
      headers: [
        { text: 'Unique ID', value: 'unique_id' },
        { text: 'Process Time', value: 'process_time' },
        { text: 'Progress', value: 'progress_step' },
        { text: 'Status', value: 'status' },
        { text: 'Files', value: 'files' },
        { text: 'Re-extract', value: 'reextract' },
        { text: 'Review', value: 'review' },
        { text: 'Result', value: 'show_result' },
      ],
      updateProgressInterval: null, // Store the interval ID
      dataFetchTimer: null,
    }
  },
  methods: {
    filter_headers() {
      return this.headers.filter(h => !h.disable)
    },
    updateTaskListProgressStep() {
      for (const task of this.tasks) {
        updateTaskProgressStep(task)
      }
      this.$forceUpdate(); // Ensure UI updates with new progress steps
    },
    scheduleNextFetch(time) {
      this.dataFetchTimer = setTimeout(async () => {
        await this.fetchTasks();
        this.scheduleNextFetch(time); // Schedule the next fetch
      }, time);
    },
    async fetchTasks() {
      this.loading = true
      try {
        const response = await this.$http.post('/api/tasks/list', {
          search: this.search,
          task_type: this.taskType,
          order_by: this.order.order_by,
          order: this.order.value,
          page: this.page,
          per_page: this.perPage
        })
        this.tasks = response.data.tasks
        this.updateTaskListProgressStep()
        this.totalTasks = response.data.total   // Adjust based on actual total
      } catch (error) {
        console.error('Error fetching tasks:', error)
      }
      this.loading = false
    },
    async restartTask(taskId) {
      this.loading = true
      try {
        await this.$http.post(`/api/tasks/${taskId}/restart`)
        await this.fetchTasks()
        alert('Task restarted successfully')
      } catch (error) {
        console.error('Error restarting task:', error)
        alert('Failed to restart task')
      }
      this.loading = false
    },
    taskTypeToPascalCase() {
      return this.taskType.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
            .map(x => x.slice(0, 1).toUpperCase() + x.slice(1).toLowerCase())
            .join('');
    },
    editTaskData(taskId, taskUniqueId) {
      this.$router.push({ name: `${this.taskTypeToPascalCase()}EditTaskData`, params: { id: taskId, uniqueId: taskUniqueId }, query: { show_review : this.$route.query.show_review } })
    },
    showResult(taskId, taskUniqueId) {
      this.$router.push({ name: `${this.taskTypeToPascalCase()}TaskResult`, params: { id: taskId, uniqueId: taskUniqueId} })
    },
    updatePage(page) {
      this.page = page
      this.fetchTasks()
    },
    updateItemsPerPage(itemsPerPage) {
      this.perPage = itemsPerPage;
      this.page = 1; // Reset to first page to avoid invalid page numbers
      this.fetchTasks(); // Fetch tasks with the new perPage value
    },
    formatTime(time) {
      if (!time) return ''
      const date = new Date(time)
      const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }
      const formatter = new Intl.DateTimeFormat('en-HK', options)
      const parts = formatter.formatToParts(date)
      return `${parts[4].value}-${parts[2].value}-${parts[0].value} ${parts[6].value}:${parts[8].value}:${parts[10].value}`
    },
    formatStatus(status) {
      if (!status) return ''
      return status
        .replace(/_/g, ' ')
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    }
  },
  mounted() {
    if (!this.showReviewColumn) {
      for (const header of this.headers) {
        if (["Review"].includes(header.text)) {
          header.disable = true;
        }
      }
    }
    this.fetchTasks() // Initial fetch
    // Set up interval to update progress steps every second for real-time updates
    this.updateProgressInterval = setInterval(() => {
      this.updateTaskListProgressStep();
    }, 1000); // Update every 1 second
    // Fetch tasks every 15 seconds to get new data
    this.scheduleNextFetch(15000)   // Start the first fetch
  },
  beforeDestroy() {
    // Clear intervals to prevent memory leaks
    if (this.updateProgressInterval) {
      clearInterval(this.updateProgressInterval)
    }
    if (this.dataFetchTimer) {
      clearTimeout(this.dataFetchTimer);
    }
  }
}
</script>
